// Billing — what family licences cost this organisation, and the invoices
// Zooylo has recorded (migration 20261022000008). No card processing: an org
// pays against an invoice; Zooylo marks it paid in the owner console.
//
// Money is formatted with Intl in the invoice's own currency. A licence costs
// from the day the family CLAIMS it (signs in), pro-rated by day — so a licence
// given to an email that never arrives costs nothing, and the screen says so.
//
// Revenue share (migration 20261023000009): the FAMILY pays the ORGANISATION
// (the retail price, recorded only); the organisation keeps the difference and
// Zooylo invoices it monthly for its share per licence-month, plus SST. The
// summary's amounts INCLUDE SST, like the invoice total the org pays.

import { useComfort } from "../ComfortContext";
import { fill, getDictionary } from "../../i18n/dictionary";
import { getPortalData } from "../../data";
import type { BillingSummary, Invoice } from "../../data/types";
import { useOrg } from "./OrgContext";
import {
  Card,
  Chip,
  ErrorPanel,
  GRID_TILES,
  LoadingLine,
  Notice,
  PageHeader,
  StatTile,
  TableCard,
  TD,
  TH,
  errorCode,
  formatDate,
  formatMoney,
  formatNumber,
  useDemoVariant,
  useLoad,
  type Tone,
} from "./ui";

type T = ReturnType<typeof getDictionary>["admin"]["billing"];

export function invoiceStatusChip(t: T, status: Invoice["status"]): { tone: Tone; label: string } {
  switch (status) {
    case "paid":
      return { tone: "good", label: t.stPaid };
    case "issued":
      return { tone: "attention", label: t.stIssued };
    case "void":
      return { tone: "muted", label: t.stVoid };
    case "draft":
      return { tone: "neutral", label: t.stDraft };
  }
}

/**
 * The arrangement in one plain sentence, from the organisation's own numbers.
 * The retail/keep half is left out when no retail price is recorded (0).
 */
export function arrangementLine(t: T, s: BillingSummary, money: (n: number) => string, pct: (n: number) => string): string {
  const vars = {
    retail: money(s.familyRetailPriceMonthly),
    keep: money(s.orgShareMonthly),
    share: money(s.familyPriceMonthly),
    rate: pct(s.sstRate),
  };
  const withRetail = s.familyRetailPriceMonthly > 0;
  const withSst = s.sstRate > 0;
  const template = withRetail
    ? withSst
      ? t.arrangement
      : t.arrangementNoSst
    : withSst
      ? t.arrangementShareOnly
      : t.arrangementShareOnlyNoSst;
  return fill(template, vars);
}

export function Billing() {
  const { locale } = useComfort();
  const t = getDictionary(locale).admin.billing;
  const lic = getDictionary(locale).admin.licences;
  const { orgId } = useOrg();
  const variant = useDemoVariant();

  const load = useLoad(async () => {
    const ds = getPortalData();
    const [summary, invoices] = await Promise.all([ds.orgBillingSummary(orgId), ds.listInvoices(orgId)]);
    return { summary, invoices };
  }, [orgId, variant]);

  const header = <PageHeader title={t.title} subtitle={t.subtitle} />;

  if (load.loading && !load.data) {
    return (
      <>
        {header}
        <LoadingLine label={t.loading} />
      </>
    );
  }
  if (load.error || !load.data?.summary) {
    const code = errorCode(load.error);
    return (
      <>
        {header}
        <div style={{ marginTop: 20 }}>
          <ErrorPanel
            title={t.errorTitle}
            message={code === "42501" ? lic.errNotAuth : lic.errGeneric}
            onRetry={load.reload}
            retryLabel={t.retry}
          />
        </div>
      </>
    );
  }

  const { summary: s, invoices } = load.data;
  const money = (n: number, cur = s.currency) => formatMoney(locale, n, cur);
  const pct = (n: number) => formatNumber(locale, n);
  const noLicences = s.familyAllowance === 0 && s.licencesLive === 0 && invoices.length === 0;

  return (
    <>
      {header}
      <p style={{ margin: "10px 0 0", fontSize: 14, color: "var(--ink-soft)" }}>{t.chargeNote}</p>

      {noLicences ? (
        <div style={{ marginTop: 20 }}>
          <Notice tone="info" title={t.freePlanTitle}>
            {t.freePlanBody}
          </Notice>
        </div>
      ) : (
        <>
          <div style={{ marginTop: 20 }}>
            <Notice tone="info" title={t.arrangementTitle}>
              <span style={{ display: "block" }} data-testid="billing-arrangement">
                {arrangementLine(t, s, (n) => money(n), pct)}
              </span>
              <span style={{ display: "block", marginTop: 6 }}>{t.arrangementCollect}</span>
            </Notice>
          </div>
          <div style={{ ...GRID_TILES, marginTop: 20 }}>
            <StatTile
              label={t.price}
              value={fill(t.perMonth, { amount: money(s.familyPriceMonthly) })}
              hint={s.sstRate > 0 ? fill(t.plusSst, { rate: pct(s.sstRate) }) : t.noSst}
            />
            <StatTile
              label={t.live}
              value={s.licencesLive}
              hint={fill(t.claimedOf, { claimed: s.licencesClaimed, pending: s.licencesPending })}
            />
            <StatTile
              label={t.monthToDate}
              value={money(s.monthToDateAmount)}
              hint={fill(t.lmLine, { n: formatNumber(locale, s.monthToDateLicenceMonths) })}
            />
            <StatTile label={t.projected} value={money(s.projectedMonthAmount)} hint={t.projectedHint} />
            <StatTile
              label={t.lastMonth}
              value={money(s.lastMonthAmount)}
              hint={fill(t.lmLine, { n: formatNumber(locale, s.lastMonthLicenceMonths) })}
            />
            <StatTile
              label={t.outstanding}
              value={money(s.outstandingAmount)}
              hint={s.outstandingAmount > 0 ? undefined : t.outstandingNone}
            />
          </div>

          {s.sstRate > 0 && (
            <p style={{ margin: "12px 0 0", fontSize: 13.5, color: "var(--ink-faint)" }}>{t.amountsIncludeSst}</p>
          )}

          {(s.contractStart || s.contractEnd) && (
            <p style={{ margin: "14px 0 0", fontSize: 14, color: "var(--ink-soft)" }}>
              <b style={{ color: "var(--ink)" }}>{t.contract}: </b>
              {s.contractEnd
                ? fill(t.contractRange, { from: formatDate(locale, s.contractStart), to: formatDate(locale, s.contractEnd) })
                : fill(t.contractFrom, { from: formatDate(locale, s.contractStart) })}
            </p>
          )}

          <section style={{ marginTop: 24 }} aria-labelledby="invoice-title">
            <h2 id="invoice-title" style={{ fontSize: 18, color: "var(--ink-deep)", margin: "0 0 10px" }}>
              {t.invoicesTitle}
            </h2>
            {invoices.length === 0 ? (
              <Card>
                <p style={{ margin: 0, color: "var(--ink-soft)" }}>{t.noInvoices}</p>
              </Card>
            ) : (
              <TableCard label={t.invoicesTitle} minWidth={860}>
                <thead>
                  <tr>
                    <th scope="col" style={TH}>{t.colNumber}</th>
                    <th scope="col" style={TH}>{t.colPeriod}</th>
                    <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.colLm}</th>
                    <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.colSubtotal}</th>
                    <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.colSst}</th>
                    <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.colTotal}</th>
                    <th scope="col" style={TH}>{t.colStatus}</th>
                    <th scope="col" style={TH}>{t.colDue}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const chip = invoiceStatusChip(t, inv.status);
                    return (
                      <tr key={inv.id}>
                        <td style={{ ...TD, fontWeight: 600, whiteSpace: "nowrap" }}>{inv.number}</td>
                        <td style={{ ...TD, whiteSpace: "nowrap" }}>
                          {fill(t.contractRange, { from: formatDate(locale, inv.periodStart), to: formatDate(locale, inv.periodEnd) })}
                        </td>
                        <td style={{ ...TD, textAlign: "end" }}>{formatNumber(locale, inv.licenceMonths)}</td>
                        <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap", textDecoration: inv.status === "void" ? "line-through" : undefined }}>
                          {money(inv.subtotal, inv.currency)}
                        </td>
                        <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap", textDecoration: inv.status === "void" ? "line-through" : undefined }}>
                          {money(inv.sstAmount, inv.currency)}
                          <span style={{ display: "block", fontSize: 12.5, color: "var(--ink-faint)" }}>{pct(inv.sstRate)}%</span>
                        </td>
                        <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap", fontWeight: 600, textDecoration: inv.status === "void" ? "line-through" : undefined }}>
                          {money(inv.amount, inv.currency)}
                        </td>
                        <td style={TD}>
                          <Chip tone={chip.tone}>{chip.label}</Chip>
                        </td>
                        <td style={{ ...TD, whiteSpace: "nowrap" }}>{formatDate(locale, inv.dueAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </TableCard>
            )}
            <p style={{ margin: "12px 0 0", fontSize: 13.5, color: "var(--ink-faint)" }}>{t.payNote}</p>
          </section>
        </>
      )}
    </>
  );
}
